import useDarkMode, { Theme } from '@hooks/useDarkMode'
import './index.scss'

import logo from 'url:@assets/images/HeaderLogo.png'
import logoDark from 'url:@assets/images/HeaderLogo-dark.png'

import icon from 'url:@assets/icons/house.svg'
import connectionsIcon from 'url:@assets/icons/connections.svg'
import playlistsIcon from 'url:@assets/icons/music.list.svg'
import launchpadIcon from 'url:@assets/icons/rocket.svg'
import rightArrowIcon from 'url:@assets/icons/arrow.right.svg'
import teamIcon from 'url:@icon/person.svg'
import eventsIcon from 'url:@assets/icons/events-icon.svg'
import settingsIcon from 'url:@assets/icons/gear.svg'
import serveIcon from 'url:@assets/icons/heart.fill.svg'
import peopleIcon from 'url:@assets/icons/people.svg'

import sidebarIcon from 'url:@icon/sidebar.svg'

import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@auth/AuthProvider'

import { slide as Menu } from 'react-burger-menu'
import { useState } from 'react'
import { StaffAction } from '@data/interfaces/church.interfaces'

type Route = {
  path: string;
  name: string;
  icon: string;
  requiredPermission: StaffAction | null;
}

// Shown only when at least one route under it is.
type Separator = {
  type: 'separator';
  label?: string;
}

type SidebarItem = Route | Separator;

const isStagingEnvironment =
  process.env.REACT_APP_SALT_BACKEND_URL?.includes('staging') === true ||
  process.env.REACT_APP_EVENTS_BACKEND_URL?.includes('staging') === true ||
  process.env.REACT_APP_SALT_PLAYLISTS_URL?.includes('staging') === true ||
  process.env.REACT_APP_SANCTUARY_API_URL?.includes('staging') === true;

const routes: SidebarItem[] = [
  {
    path: 'home',
    name: 'Home',
    icon: icon,
    requiredPermission: null
  },
  {
    path: 'launchpad',
    name: 'Launchpad',
    icon: launchpadIcon,
    requiredPermission: StaffAction.MANAGE_LAUNCHPAD
  },
  {
    type: 'separator',
    label: 'Content'
  },
  {
    path: 'playlists',
    name: 'Playlists',
    icon: playlistsIcon,
    requiredPermission: StaffAction.MANAGE_PLAYLISTS
  },
  {
    path: 'events',
    name: 'Events',
    icon: eventsIcon,
    requiredPermission: StaffAction.MANAGE_EVENTS
  },
  {
    type: 'separator',
    label: 'People'
  },
  {
    path: 'volunteering',
    name: 'Volunteering',
    icon: serveIcon,
    requiredPermission: StaffAction.MANAGE_VOLUNTEERING
  },
  {
    path: 'people',
    name: 'People',
    icon: peopleIcon,
    requiredPermission: StaffAction.MANAGE_PEOPLE
  },
  {
    type: 'separator',
    label: 'Administration'
  },
  {
    path: 'staff',
    name: 'Staff Members',
    icon: teamIcon,
    requiredPermission: StaffAction.MANAGE_STAFF
  },
  {
    path: 'connections',
    name: 'Connections',
    icon: connectionsIcon,
    requiredPermission: StaffAction.MANAGE_CONNECTIONS
  },
  {
    path: 'settings',
    name: 'Settings',
    icon: settingsIcon,
    requiredPermission: StaffAction.MANAGE_SETTINGS
  }
]

export default function Sidebar(props: {
  churchName: string;
  isMobile: boolean
}) {
  const [isOpen, setIsOpen] = useState(false);

  const sidebarContent = <SidebarContent churchName={props.churchName} closeSidebar={() => {
    setIsOpen(false)
  }} />

  return props.isMobile ? <>
    <header className='sidebar-header'>
      <a onClick={(e) => {
        e.preventDefault();

        setIsOpen(true)
      }}>
        <img src={sidebarIcon} />
      </a>
      <h1>Salt Church Hub</h1>
    </header>

    <Menu isOpen={isOpen} onStateChange={(state) => setIsOpen(state.isOpen)} className='sidebar' styles={{
      bmBurgerButton: {
        display: 'none'
      }
    }}>
      {sidebarContent}
    </Menu>
  </> : <div className='sidebar sidebar-div'>
    {sidebarContent}
  </div>
}

function SidebarContent(props: {
  churchName: string,
  closeSidebar: () => void
}) {
  const theme = useDarkMode();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();

  const path = location.pathname;
  let pathComponents = path.split('/');
  let route = pathComponents[1];

  if(route === "") {
    route = "home"
  }

  // A volunteer team's page lives at /team/{teamID}.
  if (route === "team") {
    route = "volunteering"
  }

  const canSee = (item: Route) => item.requiredPermission === null || auth.hasPermission(item.requiredPermission);

  // The routes this user's roles open, with each separator kept only if a
  // route follows it before the next one.
  const visibleItems = routes
    .map((item, index) => ({ item, index }))
    .filter(({ item, index }) => {
      if ((item as Separator).type !== 'separator') {
        return canSee(item as Route);
      }

      for (const next of routes.slice(index + 1)) {
        if ((next as Separator).type === 'separator') return false;
        if (canSee(next as Route)) return true;
      }

      return false;
    });

  return <>
    <img src={theme === Theme.LIGHT ? logo : logoDark}
      draggable={false}
      alt="Salt Icon"
      className='logo' />

    <p className='greeting'>👋 Hi, {auth.user?.name}!</p>
    <p className='churchName'>{props.churchName}</p>

    <div className='divider' />

    <ul>
      {visibleItems.map(({ item: r, index: i }) => {
        // handle separator items
        if ((r as any).type === 'separator') {
          const sep = r as Separator;

          return <li key={`sep-${i}`} className='route-separator'>
            {sep.label ? <p className='separator-label'>{sep.label}</p> : <div className='separator-line' />}
          </li>
        }

        const routeItem = r as Route;

        return <li key={i} className={route == routeItem.path ? 'selected' : ''}>
        <a href={`/${routeItem.path}`} onClick={(e) => {
          e.preventDefault();

          navigate(`/${routeItem.path}`);
          props.closeSidebar();
        }}>
          <img src={routeItem.icon} />
          <p>{routeItem.name}</p>
        </a>
      </li>
      })}
    </ul>

    <div className='divider' />

    <ul>
      <li>
        <a href="/logout" onClick={(e) => {
          e.preventDefault();

          auth.signOut(() => navigate("/"))
          props.closeSidebar();
        }}>
          <p style={{ marginRight: 8 }}>Sign Out</p>
          <img src={rightArrowIcon} />
        </a>
      </li>
    </ul>
  </>
}
